import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Boxes, Link2, LogIn, MapPin, Plus } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { AssetStatusBadge, AueBadge, DeviceStatusBadge, DueBadge, LoginEventBadge, PersonTypeBadge } from '../components/badges';
import { AssetSearch } from '../components/pickers';
import { useToast } from '../components/toast';
import { UsageChart } from '../components/UsageChart';
import { CampusBadge } from '../components/work-order-badges';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  DescriptionList,
  EmptyState,
  ErrorState,
  Field,
  PageHeader,
  PageLoader,
  Select,
  Table,
  Tabs,
  Td,
  Th,
} from '../components/ui';
import { api, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDate, formatDateTime, formatMinutes, formatRelative, personName } from '../lib/format';
import { invalidateInventory, useCategories } from '../lib/hooks';
import type { GoogleDeviceDetail } from '../lib/types';

export function GoogleDeviceDetailPage() {
  const { id } = useParams();
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const categories = useCategories();
  const [range, setRange] = useState<'30' | '90'>('30');
  const [categoryId, setCategoryId] = useState('');
  const [linkingExisting, setLinkingExisting] = useState(false);

  const query = useQuery({ queryKey: ['google-device', id], queryFn: () => api.get<GoogleDeviceDetail>(`/google-devices/${id}`) });

  const createAsset = useMutation({
    mutationFn: () => api.post<{ assetTag: string }>(`/google-devices/${id}/create-asset`, { categoryId: categoryId || undefined }),
    onSuccess: (asset) => {
      invalidateInventory(queryClient);
      toast.success(`Created asset ${asset.assetTag}`);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const linkAsset = useMutation({
    mutationFn: (assetId: string) => api.put(`/assets/${assetId}/google-device`, { googleDeviceId: id }),
    onSuccess: () => {
      invalidateInventory(queryClient);
      setLinkingExisting(false);
      toast.success('Linked to asset');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  if (query.isLoading) return <PageLoader />;
  if (query.error || !query.data) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;

  const device = query.data;
  const { network } = device;
  const canWrite = can('TECHNICIAN');
  const active = device.asset?.activeCheckout;

  return (
    <>
      <PageHeader
        back={{ to: '/devices', label: 'ChromeOS devices' }}
        title={<span className="font-mono">{device.serialNumber ?? device.deviceId}</span>}
        description={device.model}
        meta={
          <>
            <DeviceStatusBadge status={device.status} removed={!!device.removedFromGoogleAt} />
            <CampusBadge location={network.location} />
            {device.orgUnitPath && <Badge>{device.orgUnitPath}</Badge>}
            <Badge tone="gray">Synced {formatRelative(device.lastSync)}</Badge>
          </>
        }
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader
              title="Daily active time"
              description={`${formatMinutes(device.activeMinutes7d)} in the last 7 days · ${formatMinutes(device.activeMinutes30d)} in 30 days`}
              actions={
                <Tabs<'30' | '90'>
                  value={range}
                  onChange={setRange}
                  tabs={[
                    { value: '30', label: '30 days' },
                    { value: '90', label: '90 days' },
                  ]}
                  className="border-none"
                />
              }
            />
            <div className="p-5">
              <UsageChart usage={device.usage} days={Number(range)} />
            </div>
          </Card>

          <Card>
            <CardHeader
              title="Sign-in history"
              description={
                network.configured
                  ? 'From the Google Admin audit log. Location compares the sign-in’s public IP with your school’s IP addresses.'
                  : 'From the Google Admin audit log'
              }
            />
            {device.loginEvents.length === 0 ? (
              <EmptyState icon={<LogIn />} title="No sign-in events recorded" />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>When</Th>
                    <Th>Event</Th>
                    <Th>User</Th>
                    {network.configured && <Th>Location</Th>}
                    <Th>Public IP</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {device.loginEvents.map((event) => (
                    <tr key={event.id}>
                      <Td className="whitespace-nowrap">{formatDateTime(event.occurredAt)}</Td>
                      <Td>
                        <LoginEventBadge type={event.eventType} />
                      </Td>
                      <Td>
                        {event.person ? (
                          <Link to={`/people/${event.person.id}`} className="font-medium text-slate-900 hover:text-brand-700">
                            {personName(event.person)}
                          </Link>
                        ) : null}
                        <p className="text-xs text-slate-500">{event.userEmail}</p>
                      </Td>
                      {network.configured && (
                        <Td className="whitespace-nowrap">
                          <CampusBadge location={event.campus} />
                        </Td>
                      )}
                      <Td className="font-mono text-xs text-slate-500">{event.ipAddress ?? '—'}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader title="Network location" description="As of the device’s last check-in with Google" />
            <div className="space-y-3 p-5">
              {network.configured ? (
                <>
                  <CampusBadge location={network.location} />
                  <DescriptionList
                    columns={1}
                    items={[
                      { label: 'Public IP', value: network.publicIp && <span className="font-mono">{network.publicIp}</span> },
                      { label: 'Local IP', value: network.localIp && <span className="font-mono">{network.localIp}</span> },
                      { label: 'Reported', value: network.asOf && `${formatRelative(network.asOf)} · ${formatDateTime(network.asOf)}` },
                    ]}
                  />
                </>
              ) : (
                <div className="flex gap-3 text-sm text-slate-600">
                  <MapPin className="mt-0.5 size-4 shrink-0 text-slate-400" />
                  <p>
                    Public IP <span className="font-mono">{network.publicIp ?? 'not reported'}</span>.{' '}
                    {canWrite ? (
                      <>
                        To see whether devices are on or off campus, add your school’s public IP addresses under{' '}
                        <Link to="/settings" className="font-medium text-brand-700 hover:underline">
                          Administration › Settings
                        </Link>
                        .
                      </>
                    ) : (
                      'Your school hasn’t entered its public IP addresses yet, so on/off campus isn’t available.'
                    )}
                  </p>
                </div>
              )}
            </div>
          </Card>

          <Card>
            <CardHeader title="Asset record" />
            <div className="p-5">
              {device.asset ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <Link to={`/assets/${device.asset.id}`} className="font-mono text-sm font-semibold text-brand-800 hover:underline">
                      {device.asset.assetTag}
                    </Link>
                    <AssetStatusBadge status={device.asset.status} />
                  </div>
                  <p className="text-sm text-slate-600">{device.asset.name}</p>
                  {active ? (
                    <div className="rounded-lg bg-slate-50 px-3 py-2 text-sm">
                      <p className="text-slate-500">Checked out to</p>
                      <p className="font-medium text-slate-900">
                        {active.personId ? (
                          <Link to={`/people/${active.personId}`} className="hover:text-brand-700">
                            {active.assigneeLabel}
                          </Link>
                        ) : (
                          active.assigneeLabel
                        )}
                      </p>
                      <DueBadge dueAt={active.dueAt} />
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500">Not checked out.</p>
                  )}
                </div>
              ) : canWrite ? (
                <div className="space-y-4">
                  <p className="text-sm text-slate-600">This device isn’t linked to an asset yet.</p>
                  {linkingExisting ? (
                    <>
                      <AssetSearch inline placeholder="Find an asset to link" onSelect={(asset) => linkAsset.mutate(asset.id)} />
                      <Button size="sm" variant="ghost" onClick={() => setLinkingExisting(false)}>
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <>
                      <Field label="Category">
                        <Select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                          <option value="">Chromebook (default)</option>
                          {categories.data?.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                        </Select>
                      </Field>
                      <div className="flex flex-wrap gap-2">
                        <Button variant="primary" icon={<Plus />} loading={createAsset.isPending} onClick={() => createAsset.mutate()}>
                          Create asset
                        </Button>
                        <Button icon={<Link2 />} onClick={() => setLinkingExisting(true)}>
                          Link existing
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <EmptyState icon={<Boxes />} title="Not linked to an asset" />
              )}
            </div>
          </Card>

          <Card>
            <CardHeader title="Recent users" description="Newest first, as reported by the device" />
            {device.recentUsers.length === 0 ? (
              <p className="px-5 py-4 text-sm text-slate-500">No recent users reported.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {device.recentUsers.map((user, index) => (
                  <li key={`${user.email}-${index}`} className="flex items-center justify-between gap-2 px-5 py-2.5 text-sm">
                    <div className="min-w-0">
                      {user.person ? (
                        <Link to={`/people/${user.person.id}`} className="font-medium text-slate-900 hover:text-brand-700">
                          {personName(user.person)}
                        </Link>
                      ) : (
                        <span className="text-slate-700">{user.email ?? 'Unmanaged user'}</span>
                      )}
                      {user.person && <p className="truncate text-xs text-slate-500">{user.email}</p>}
                    </div>
                    {user.person && <PersonTypeBadge type={user.person.type} />}
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card>
            <CardHeader title="Device details" />
            <div className="p-5">
              <DescriptionList
                columns={1}
                items={[
                  { label: 'Auto-update expiration', value: <AueBadge date={device.autoUpdateExpiration} /> },
                  { label: 'ChromeOS version', value: device.osVersion && `${device.osVersion} (${device.platformVersion})` },
                  { label: 'Firmware', value: device.firmwareVersion },
                  { label: 'Boot mode', value: device.bootMode },
                  { label: 'Assigned user (Admin console)', value: device.annotatedUser },
                  { label: 'Asset ID (Admin console)', value: device.annotatedAssetId },
                  { label: 'Location (Admin console)', value: device.annotatedLocation },
                  { label: 'MAC address', value: device.macAddress && <span className="font-mono">{device.macAddress}</span> },
                  { label: 'Enrolled', value: device.lastEnrollmentTime && formatDate(device.lastEnrollmentTime) },
                  { label: 'Manufactured', value: device.manufactureDate },
                  { label: 'Google device ID', value: <span className="font-mono text-xs">{device.deviceId}</span> },
                  { label: 'Notes', value: device.notes },
                ]}
              />
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}
